import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import CaseCard from "@/components/feature/CaseCard";
import PageHeader from "@/components/base/PageHeader";
import CaseToolbar from "@/pages/cases/components/CaseToolbar";
import { usePageMeta, SITE_ORIGIN } from "@/hooks/usePageMeta";
import { caseArticles, caseCategories } from "@/mocks/cases";
import { siteInfo } from "@/mocks/site";

export default function CasesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "全部";
  const [keyword, setKeyword] = useState(searchParams.get("keyword") || "");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return caseArticles.filter((item) => {
      const matchCategory = activeCategory === "全部" || item.category === activeCategory;
      const matchKeyword =
        kw === "" ||
        item.title.toLowerCase().includes(kw) ||
        item.excerpt.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  const handleCategoryChange = (category: string) => {
    const next = new URLSearchParams(searchParams);
    if (category === "全部") {
      next.delete("category");
    } else {
      next.set("category", category);
    }
    setSearchParams(next, { replace: true });
  };

  usePageMeta({
    title: `调查档案｜网暴黑料施暴者、键盘侠与水军深度复盘 - ${siteInfo.name}`,
    description:
      "按主题浏览黑料显微镜的全部调查档案：施暴者画像、水军产业、舆论操纵、平台生态、法律边界与受害者重建，共 6 个方向、多份可核验的深度复盘。",
    keywords: "网暴黑料档案,施暴者画像,水军产业,舆论操纵,平台生态,法律边界,受害者重建",
    path: "/cases",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "调查档案",
      url: `${SITE_ORIGIN}/cases`,
      inLanguage: "zh-CN",
      description: "按主题浏览黑料显微镜的全部调查档案。",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "调查档案", item: `${SITE_ORIGIN}/cases` },
        ],
      },
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: caseArticles.length,
        itemListElement: caseArticles.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE_ORIGIN}/cases/${item.slug}`,
          name: item.title,
        })),
      },
    },
  });

  return (
    <Layout>
      <PageHeader
        crumbs={[{ label: "首页", to: "/" }, { label: "调查档案" }]}
        eyebrow="Case Files"
        title="调查档案：把网暴黑料拆到看得见为止"
        description="每一份档案都从一个具体问题出发，梳理事件脉络、施暴者动机与背后的利益结构。你可以按主题筛选，也可以直接搜索关键词。"
      >
        <CaseToolbar
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
          keyword={keyword}
          onKeywordChange={setKeyword}
          resultCount={filtered.length}
        />
      </PageHeader>

      <section className="w-full bg-background-50 py-12 md:py-16">
        <div className="max-w-content mx-auto px-4 md:px-6">
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filtered.map((item) => (
                <CaseCard key={item.slug} article={item} />
              ))}
            </div>
          ) : (
            <div className="py-16 text-center rounded-lg border border-background-200 bg-background-100">
              <i className="ri-file-search-line text-4xl text-foreground-400" aria-hidden="true"></i>
              <p className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                没有匹配的档案
              </p>
              <p className="mt-2 text-sm text-foreground-600">
                换个关键词，或者切换到其他主题看看。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  handleCategoryChange("全部");
                }}
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
              >
                <i className="ri-refresh-line" aria-hidden="true"></i>
                重置筛选条件
              </button>
            </div>
          )}

          <div className="mt-10 md:mt-12 p-6 md:p-8 rounded-lg bg-secondary-50 border border-secondary-200 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <h2 className="font-heading text-lg md:text-xl font-bold text-secondary-950">
                手上有值得核实的线索？
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-secondary-900/80 max-w-xl">
                我们只对能够提供公开可核验材料的事件展开分析，不接受匿名指控具体个人。投稿前请先阅读我们的内容原则。
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
              >
                <i className="ri-mail-send-line" aria-hidden="true"></i>
                投递线索
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer bg-background-50 border border-secondary-300 text-secondary-900 hover:border-secondary-500 transition-colors"
              >
                阅读内容原则
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}